import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowRight, 
  ChevronRight, 
  CheckCircle2, 
  Megaphone,
  Zap,
  Target,
  BarChart3,
  TrendingUp,
  DollarSign,
  Eye,
  ShoppingCart
} from 'lucide-react';

const FlipkartAdvertising = () => {
  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Flipkart Advertising & PPC Management Services | Obiquiste</title>
        <meta name="description" content="Expert Flipkart advertising management: product ads, brand ads & campaign optimization. Increase visibility & sales with data-driven ad strategies." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/flipkart-advertising-management" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-100 py-4 border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-slate-700 hover:text-primary font-medium">Home</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/services" className="text-slate-700 hover:text-primary font-medium">Services</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <Link to="/flipkart-account-management-services" className="text-slate-700 hover:text-primary font-medium">Flipkart</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <span className="text-primary font-semibold">Advertising Management</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section 
        className="py-16 md:py-24 lg:py-32 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.03) 0%, rgba(234, 179, 8, 0.03) 100%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)'
        }}
      >
        <div className="absolute top-20 right-10 w-72 h-72 bg-orange-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-6">
                Flipkart Advertising <span className="bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">Management Services</span>
              </h1>
              <p className="text-xl md:text-2xl font-semibold text-slate-800 mb-4">
                Professional Ad Management to Maximize Your Flipkart Sales
              </p>
              <p className="text-lg leading-relaxed text-slate-600 mb-8">
                Flipkart advertising helps increase product visibility and sales. Amazing Sales provides professional advertising management services for Flipkart sellers to maximize their return on ad spend and drive sustainable growth.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact?service=flipkart-ads">
                  <Button className="bg-primary text-white hover:bg-primary-600 shadow-lg hover:shadow-xl px-8 py-6 rounded-full text-base font-semibold">
                    Get Free Ads Consultation
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/contact?service=flipkart-ads">
                  <Button variant="outline" className="border-2 border-slate-300 hover:border-primary hover:bg-slate-50 px-8 py-6 rounded-full text-base font-semibold">
                    Talk to Ads Expert
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="absolute -inset-4 bg-gradient-to-r from-orange-400/20 to-yellow-400/20 rounded-3xl blur-2xl"></div>
                <div className="relative bg-white rounded-2xl shadow-2xl p-8">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-orange-50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold text-orange-600">4.2x</div>
                      <div className="text-sm text-slate-600">Avg. ROAS</div>
                    </div>
                    <div className="bg-yellow-50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold text-yellow-600">+180%</div>
                      <div className="text-sm text-slate-600">Sales Growth</div>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold text-green-600">-35%</div>
                      <div className="text-sm text-slate-600">Cost Per Click</div>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-4 text-center">
                      <div className="text-3xl font-bold text-blue-600">Daily</div>
                      <div className="text-sm text-slate-600">Optimization</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Why Flipkart Advertising Matters
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              In today's competitive marketplace, organic visibility alone isn't enough. Flipkart's advertising platform offers powerful tools to put your products in front of the right customers at the right time. However, without proper management, ad spend can quickly become inefficient.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              Amazing Sales brings expertise in Flipkart advertising to help you create campaigns that deliver results. Our data-driven approach ensures every rupee spent contributes to your growth objectives.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Campaign Creation Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-orange-50 to-yellow-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mb-6">
                <Zap className="w-8 h-8 text-orange-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6 text-slate-900">
                Campaign Creation
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Our team creates advertising campaigns by selecting the right products for promotion and setting up campaigns properly to achieve optimal results from the start.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                We don't just launch campaigns—we strategically build them based on your business goals, target audience, and competitive landscape.
              </p>
              
              <div className="space-y-4">
                {[
                  { title: 'Product Selection', desc: 'Identifying high-potential products for promotion' },
                  { title: 'Campaign Structure', desc: 'Organizing campaigns for maximum efficiency' },
                  { title: 'Targeting Configuration', desc: 'Setting up audience and keyword targeting' },
                  { title: 'Budget Allocation', desc: 'Strategic distribution of ad spend' },
                  { title: 'Bid Strategy', desc: 'Competitive bidding for optimal placement' },
                  { title: 'Ad Creative', desc: 'Compelling ad content that converts' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-slate-900">{item.title}:</span>
                      <span className="text-slate-600 ml-1">{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 shadow-xl border border-orange-100"
            >
              <h3 className="font-heading text-2xl font-bold mb-6 text-slate-900">
                Campaign Types We Manage
              </h3>
              <div className="space-y-4">
                {[
                  { name: 'Sponsored Products', desc: 'Promote individual products in search results', icon: ShoppingCart },
                  { name: 'Product Listing Ads', desc: 'Showcase products on category pages', icon: Eye },
                  { name: 'Brand Ads', desc: 'Build brand awareness and visibility', icon: Megaphone },
                  { name: 'Display Ads', desc: 'Reach customers across Flipkart platform', icon: Target }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-4 bg-orange-50 rounded-xl p-4">
                    <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{item.name}</h4>
                      <p className="text-sm text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Campaign Optimization Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-8 border border-blue-100"
            >
              <h3 className="font-heading text-2xl font-bold mb-6 text-slate-900">
                Our Optimization Techniques
              </h3>
              <div className="space-y-4">
                {[
                  { step: 'Bid Adjustments', desc: 'Fine-tune bids based on performance data' },
                  { step: 'Keyword Optimization', desc: 'Add winners, remove underperformers' },
                  { step: 'Negative Keywords', desc: 'Block irrelevant search terms' },
                  { step: 'Budget Reallocation', desc: 'Shift spend to top performers' },
                  { step: 'Dayparting', desc: 'Optimize for peak shopping hours' },
                  { step: 'A/B Testing', desc: 'Test different strategies for best results' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-4 bg-white rounded-lg p-4 shadow-sm">
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">{idx + 1}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-slate-900">{item.step}:</span>
                      <span className="text-slate-600 ml-1">{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6 text-slate-900">
                Campaign Optimization
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                We optimize advertising campaigns by adjusting bids, improving targeting, and managing budgets to achieve better results and maximize return on investment.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Optimization isn't a one-time task—it's a continuous process. Our team constantly analyzes campaign data to identify opportunities for improvement and implements changes that drive better performance.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  'Bid adjustments',
                  'Targeting improvement',
                  'Budget management',
                  'ROI maximization',
                  'Cost reduction',
                  'Conversion optimization',
                  'Placement refinement',
                  'Audience expansion'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-blue-500" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Performance Monitoring Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                <BarChart3 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6 text-slate-900">
                Performance Monitoring
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Our team regularly reviews advertising campaigns and checks campaign performance on an alternate basis to ensure ads are running efficiently and delivering better results.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                We don't just set campaigns and forget them. Regular monitoring allows us to catch issues early, capitalize on opportunities, and ensure your ad spend is working as hard as possible.
              </p>
              
              <div className="space-y-4">
                {[
                  { title: 'Regular Reviews', desc: 'Scheduled analysis of campaign performance' },
                  { title: 'Performance Analysis', desc: 'Deep dive into metrics and trends' },
                  { title: 'Efficiency Tracking', desc: 'Monitoring cost efficiency and ROAS' },
                  { title: 'Result Optimization', desc: 'Acting on insights to improve outcomes' },
                  { title: 'Reporting', desc: 'Clear, actionable performance reports' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-slate-900">{item.title}:</span>
                      <span className="text-slate-600 ml-1">{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 shadow-xl border border-green-100"
            >
              <h3 className="font-heading text-2xl font-bold mb-6 text-slate-900">
                Key Metrics We Track
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { metric: 'ROAS', desc: 'Return on Ad Spend', icon: DollarSign, color: 'green' },
                  { metric: 'CTR', desc: 'Click-Through Rate', icon: Eye, color: 'blue' },
                  { metric: 'CPC', desc: 'Cost Per Click', icon: Target, color: 'orange' },
                  { metric: 'Conversions', desc: 'Total Sales', icon: ShoppingCart, color: 'purple' },
                  { metric: 'Impressions', desc: 'Ad Views', icon: TrendingUp, color: 'cyan' },
                  { metric: 'ACoS', desc: 'Ad Cost of Sales', icon: BarChart3, color: 'pink' }
                ].map((item, idx) => (
                  <div key={idx} className={`bg-${item.color}-50 rounded-xl p-4 text-center`}>
                    <item.icon className={`w-8 h-8 text-${item.color}-600 mx-auto mb-2`} />
                    <div className="font-bold text-slate-900">{item.metric}</div>
                    <div className="text-xs text-slate-600">{item.desc}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Advertising Goals Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Our Advertising Management Goals
            </h2>
            <p className="text-lg text-slate-300 max-w-3xl mx-auto">
              The goal of our advertising management is to improve product visibility, increase conversions, and generate higher sales on Flipkart while maintaining optimal advertising costs.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { 
                title: 'Improve Visibility', 
                desc: 'Get your products seen by more customers through strategic ad placements',
                icon: Eye
              },
              { 
                title: 'Increase Conversions', 
                desc: 'Turn views into actual sales with optimized campaigns and targeting',
                icon: ShoppingCart
              },
              { 
                title: 'Generate Higher Sales', 
                desc: 'Grow your revenue on Flipkart with data-driven advertising strategies',
                icon: TrendingUp
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white/10 rounded-2xl p-8 text-center backdrop-blur-sm border border-white/10"
              >
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-slate-300">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-orange-50 to-yellow-50 rounded-3xl p-12 border border-orange-200">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Ready to Maximize Your Flipkart Ad Performance?
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Let Amazing Sales manage your Flipkart advertising campaigns for better visibility, more conversions, and higher sales. Get started with a free consultation today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?service=flipkart-ads">
                <Button className="bg-primary text-white hover:bg-primary-600 px-10 py-7 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl">
                  Get Free Ads Consultation
                  <ArrowRight className="ml-2" size={24} />
                </Button>
              </Link>
              <Link to="/flipkart-account-management-services">
                <Button variant="outline" className="border-2 border-slate-300 hover:border-primary px-10 py-7 rounded-full text-lg font-semibold">
                  View All Flipkart Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FlipkartAdvertising;
